import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import MagnifyingGlassIcon from '@mui/icons-material/Search';
import {Box, Paper, Tooltip} from '@mui/material';
import {Attribution, MousePosition, ScaleLine} from 'ol/control';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';

import CopyButton from '../common/CopyButton.jsx';
import {
  AttributionContainer,
  CloseButton,
  CurrentLocation,
  Label,
  ScaleContainer,
} from './StatusBarComponents.jsx';

import {clearMapPin} from '@app/actions/map';
import {setPreference} from '@app/actions/settings';
import {trackEvent} from '@app/analytics';

import {coordinateSystems, preferenceGroups} from '@app/constants';
import useMapViewParams from '@app/hooks/useMapViewParams';
import formatLocation from '@app/util/format-location';

const COORDINATE_SYSTEM_ORDER = [
  coordinateSystems.dd,
  coordinateSystems.dms,
  coordinateSystems.mgrs,
];

const StatusBar = ({addMapControl, ...props}) => {
  const {zoom} = useMapViewParams();

  const [res, setRes] = useState();
  const mousePositionRef = useRef();
  const mousePositionCtl = useRef();
  const coordinate = useRef(null);

  const scaleLineCtl = useRef();
  const scaleLineRef = useRef();

  const attributionCtl = useRef();
  const attributionRef = useRef();

  const isDMS = props.coordinateSystem === coordinateSystems.dms;
  const isMGRS = props.coordinateSystem === coordinateSystems.mgrs;
  const nextSystem = current =>
    COORDINATE_SYSTEM_ORDER[(COORDINATE_SYSTEM_ORDER.indexOf(current) + 1) % 3];

  const measurementUnits =
    props.measurementSystem === 'usc' ? 'us' : props.measurementSystem;

  const pinnedLocation = props.pinnedLocation
    ? formatLocation(props.pinnedLocation, props.coordinateSystem, res)
    : '';

  const setCoordinatePreference = () => {
    const getSystem = nextSystem(props.coordinateSystem);
    props.setPreference({
      key: preferenceGroups.coordinateSystem,
      value: getSystem,
    });
    trackEvent('Coordinate System changed', {
      system: getSystem,
    });
  };

  const coordinateFormatter = useCallback(
    coord => {
      coordinate.current = coord;

      if (isMGRS) {
        const resolution = mousePositionCtl.current
          .getMap()
          .getView()
          .getResolution();
        setRes(resolution);
      }

      return formatLocation(coord, props.coordinateSystem, res);
    },
    [isMGRS, res, props.coordinateSystem]
  );

  const onAddMapControls = () => {
    mousePositionCtl.current = new MousePosition({
      className: 'pl-mouse-position',
      coordinateFormat: coordinateFormatter,
      target: mousePositionRef.current,
      undefinedHTML: '',
    });
    addMapControl(mousePositionCtl.current);

    scaleLineCtl.current = new ScaleLine({
      className: 'pl-scale-line',
      target: scaleLineRef.current,
      units: measurementUnits || 'metric',
    });

    addMapControl(scaleLineCtl.current);

    attributionCtl.current = new Attribution({
      target: attributionRef.current,
      collapsible: false,
    });

    addMapControl(attributionCtl.current);

    return () => {
      mousePositionCtl.current.getMap().removeControl(mousePositionCtl.current);
    };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(onAddMapControls, []);

  useEffect(() => {
    mousePositionCtl.current.setCoordinateFormat(coordinateFormatter);
    if (coordinate.current) {
      const map = mousePositionCtl.current.getMap();
      const pixel = map.getPixelFromCoordinate(coordinate.current);
      mousePositionCtl.current.updateHTML_(pixel);
    }
  }, [coordinateFormatter, isDMS]);

  useEffect(() => {
    scaleLineCtl.current.setUnits(measurementUnits);
  }, [measurementUnits]);

  return (
    <Paper
      elevation={0}
      square
      sx={{
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        height: theme => theme.spacing(3),
        paddingRight: 2,
        overflow: 'hidden',
        zIndex: 'appBar',
        position: 'relative',
      }}
      className="pl-mode-dark"
    >
      <AttributionContainer ref={attributionRef} noWrap />
      {pinnedLocation && (
        <Box
          sx={{
            display: 'flex',
          }}
        >
          <Label noWrap>
            <GpsFixedIcon fontSize="inherit" />
            {pinnedLocation}
          </Label>
          <CopyButton
            buttonStyleOverrides={{
              fontSize: theme => theme.typography.body2.fontSize,
            }}
            onCopy={() => trackEvent('Copied coordinates to clipboard')}
            showNotification={false}
            size="small"
            textToCopy={pinnedLocation}
            title="Coordinates"
          />
          <CloseButton onClick={() => props.clearMapPin()} title="Remove" />
        </Box>
      )}
      <CurrentLocation
        ref={mousePositionRef}
        onClick={setCoordinatePreference}
      />
      <Label data-testid="zoom-level">
        <MagnifyingGlassIcon fontSize="inherit" />
        <Tooltip title="zoom level">
          <span>{parseFloat(zoom.toFixed(2))}</span>
        </Tooltip>
      </Label>
      <ScaleContainer ref={scaleLineRef} />
    </Paper>
  );
};

StatusBar.propTypes = {
  addMapControl: PropTypes.func,
  coordinateSystem: PropTypes.string.isRequired,
  measurementSystem: PropTypes.string.isRequired,
  clearMapPin: PropTypes.func,
  pinnedLocation: PropTypes.array,
  setPreference: PropTypes.func,
};

const mapStateToProps = state => ({
  coordinateSystem: state.settings.coordinateSystem,
  measurementSystem: state.settings.measurementSystem,
  pinnedLocation: state.map.mapPin,
});

const mapDispatch = {
  clearMapPin,
  setPreference,
};

export default connect(mapStateToProps, mapDispatch)(StatusBar);
